import React, { Dispatch, SetStateAction } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import Typography from "@mui/material/Typography";

import { useConfig } from "../../contexts/ConfigContext";
import { JobOperation, Profile, TemplateSetAggregate } from "../../services/api";
import {
  BackfillSearchOption,
  OperationSearchParams,
  OperationType,
  RegisterAccounts,
} from "../../services/api/JobOperation";
import InputGroup from "../shared/InputGroup";
import CreativeIDFilter from "./CreativeIDsFilter";
import JobIDFilter from "./JobIDsFilter";
import ParamsFilter from "./ParamsFilter";

const MAX_LABEL_LENGTH = 54;

type CreateBackFillFormProps = {
  availableProfiles: Profile[];
  availableSets: TemplateSetAggregate[];
  jobOperation: JobOperation;
  setJobOperation: Dispatch<SetStateAction<JobOperation>>;
  onSearchClick: () => void;
};

const CreateBackfillForm: React.FC<CreateBackFillFormProps> = ({
  availableProfiles,
  availableSets,
  jobOperation,
  setJobOperation,
  onSearchClick,
}) => {
  const { config } = useConfig();
  const [searchType, setSearchType] = React.useState<BackfillSearchOption>(BackfillSearchOption.PARAMS);

  const toggleAccount = (accountToToggle: RegisterAccounts, checked: boolean) => {
    const { accounts, primary_regions, secondary_regions } = jobOperation;

    // Handle account toggling
    const updatedAccounts = checked
      ? [...accounts, accountToToggle]
      : accounts.filter((account) => account !== accountToToggle);

    // Handle region toggling based on the account type
    const updatedPrimaryRegions =
      accountToToggle === "primary" ? (checked ? config.regions.primary_regions : []) : primary_regions;
    const updatedSecondaryRegions =
      accountToToggle === "secondary" ? (checked ? config.regions.secondary_regions : []) : secondary_regions;

    setJobOperation(
      (prevJobOperation) =>
        new JobOperation({
          ...prevJobOperation,
          accounts: updatedAccounts,
          primary_regions: updatedPrimaryRegions,
          secondary_regions: updatedSecondaryRegions,
        }),
    );
  };

  const toggleProfile = (profileToToggle: Profile, checked: boolean) => {
    setJobOperation((prevJobOperation) => {
      const newProfiles = new Set(prevJobOperation.profiles);
      if (checked) {
        newProfiles.add(profileToToggle);
      } else {
        newProfiles.delete(profileToToggle);
      }

      return new JobOperation({
        ...prevJobOperation,
        profiles: Array.from(newProfiles),
      });
    });
  };

  const handleSearchType = (_event: React.MouseEvent<HTMLElement>, newSearchType: BackfillSearchOption) => {
    setSearchType(newSearchType);
    setJobOperation(
      (prevJobOperation) =>
        new JobOperation({
          ...prevJobOperation,
          backfillSearchOption: newSearchType,
        }),
    );
  };

  const handleOperationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const operationType = event.target.value as OperationType;
    setJobOperation((prevJobOperation) => {
      if (operationType === OperationType.REGISTER) {
        return new JobOperation({
          ...prevJobOperation,
          operation: operationType,
          accounts: [],
          primary_regions: [],
          secondary_regions: [],
        });
      } else {
        return new JobOperation({
          ...prevJobOperation,
          operation: operationType,
          accounts: config.registerAccounts,
          primary_regions: config.regions.primary_regions,
          secondary_regions: config.regions.secondary_regions,
        });
      }
    });
  };

  const handlePrimaryRegionsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setJobOperation((prevJobOperation) => {
      const newPrimaryRegions = new Set(prevJobOperation.primary_regions);
      const newAccounts = new Set(prevJobOperation.accounts);
      if (event.target.checked) {
        newPrimaryRegions.add(event.target.value);
        newAccounts.add("primary");
      } else {
        newPrimaryRegions.delete(event.target.value);
        if (newPrimaryRegions.size === 0) {
          newAccounts.delete("primary");
        }
      }

      return new JobOperation({
        ...prevJobOperation,
        primary_regions: Array.from(newPrimaryRegions),
        accounts: Array.from(newAccounts),
      });
    });
  };

  const handleSecondaryRegionsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setJobOperation((prevJobOperation) => {
      const newSecondaryRegions = new Set(prevJobOperation.secondary_regions);
      const newAccounts = new Set(prevJobOperation.accounts);
      if (event.target.checked) {
        newSecondaryRegions.add(event.target.value);
        newAccounts.add("secondary");
      } else {
        newSecondaryRegions.delete(event.target.value);
        if (newSecondaryRegions.size === 0) {
          newAccounts.delete("secondary");
        }
      }

      return new JobOperation({
        ...prevJobOperation,
        secondary_regions: Array.from(newSecondaryRegions),
        accounts: Array.from(newAccounts),
      });
    });
  };

  const handleOverwriteExistingTranscodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setJobOperation(
      (prevJobOperation) =>
        new JobOperation({
          ...prevJobOperation,
          overwrite: event.target.checked,
        }),
    );
  };

  const setParams = (params: OperationSearchParams | null) => {
    setJobOperation(
      (prevJobOperation) =>
        new JobOperation({
          ...prevJobOperation,
          params: params,
        }),
    );
  };

  const setCreativeIds = (creativeIDs: string[]) => {
    setJobOperation(
      (prevJobOperation) =>
        new JobOperation({
          ...prevJobOperation,
          creative_ids: creativeIDs,
        }),
    );
  };

  const setJobIds = (jobIDs: string[]) => {
    setJobOperation(
      (prevJobOperation) =>
        new JobOperation({
          ...prevJobOperation,
          ids: jobIDs,
        }),
    );
  };

  const renderSearchComponent = () => {
    switch (jobOperation.backfillSearchOption) {
      case BackfillSearchOption.CREATIVE_ID:
        return <CreativeIDFilter setCreativeIDs={setCreativeIds} />;
      case BackfillSearchOption.JOB_ID:
        return <JobIDFilter setJobIds={setJobIds} />;
      case BackfillSearchOption.PARAMS:
      default:
        return <ParamsFilter setParams={setParams} availableSets={availableSets} />;
    }
  };

  const truncateLabel = (label: string) =>
    label.length > MAX_LABEL_LENGTH ? label.substring(0, MAX_LABEL_LENGTH - 3) + "..." : label;

  return (
    <Grid container mt={3}>
      <Grid item md={4} xs={12}>
        <Typography sx={{ fontSize: "20pt" }}>Create Backfill</Typography>
      </Grid>
      <Grid item md={true} xs={true}>
        <ToggleButtonGroup color="primary" exclusive fullWidth value={searchType} onChange={handleSearchType}>
          <ToggleButton
            disabled={jobOperation.backfillSearchOption === BackfillSearchOption.PARAMS}
            value={BackfillSearchOption.PARAMS}
            data-testid={"search-option-params"}
          >
            Backfill Via Search Filters
          </ToggleButton>
          <ToggleButton
            disabled={jobOperation.backfillSearchOption === BackfillSearchOption.JOB_ID}
            value={BackfillSearchOption.JOB_ID}
            data-testid={"search-option-job"}
          >
            Backfill Via Job IDs
          </ToggleButton>
          <ToggleButton
            disabled={jobOperation.backfillSearchOption === BackfillSearchOption.CREATIVE_ID}
            value={BackfillSearchOption.CREATIVE_ID}
            data-testid={"search-option-creative"}
          >
            Backfill Via Creative IDs
          </ToggleButton>
        </ToggleButtonGroup>
        <Card sx={{ minWidth: 275, border: "1px solid grey", mb: "20pt", mt: "20pt" }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Operation
            </Typography>
            <RadioGroup value={jobOperation.operation} onChange={handleOperationChange} row={true}>
              <Stack direction={{ xs: "column", sm: "row" }} sx={{ width: "100%" }} spacing={{ xs: 2 }}>
                <FormControlLabel
                  value={OperationType.TRANSCODE}
                  control={<Radio data-testid={"select-operation-transcode"} />}
                  label="Transcode and Re-register"
                  sx={{ flex: "1", justifyContent: "center" }}
                />
                <FormControlLabel
                  value={OperationType.REGISTER}
                  control={<Radio data-testid={"select-operation-reregister"} />}
                  label="Re-Register"
                  sx={{ flex: "1", justifyContent: "center" }}
                />
                <FormControlLabel
                  value={OperationType.DEACTIVATE}
                  control={<Radio data-testid={"select-operation-deactivate"} />}
                  label="Deactivate"
                  sx={{ flex: "1", justifyContent: "center" }}
                />
              </Stack>
            </RadioGroup>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 275, border: "1px solid grey", mb: "20pt", mt: "20pt" }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Search Parameters
            </Typography>
            <Box sx={{ margin: "20px 0px 0px auto" }}>{renderSearchComponent()}</Box>
          </CardContent>
        </Card>
        {jobOperation.operation !== OperationType.DEACTIVATE && (
          <Card sx={{ minWidth: 275, border: "1px solid grey", mb: "20pt" }}>
            <CardContent sx={{ pb: "0pt" }}>
              <Typography variant="h6" gutterBottom>
                Backfill Parameters
              </Typography>
              {jobOperation.operation === OperationType.REGISTER && (
                <>
                  <InputGroup label="Accounts">
                    <RadioGroup row={true}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            onChange={(e) => toggleAccount("primary", e.target.checked)}
                            value={"primary"}
                            checked={jobOperation.accounts.includes("primary")}
                            data-testid={"account-primary"}
                          />
                        }
                        label={config.primaryAccountName}
                      />
                      {config.secondaryAccountName && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              onChange={(e) => toggleAccount("secondary", e.target.checked)}
                              value={"secondary"}
                              checked={jobOperation.accounts.includes("secondary")}
                              data-testid={"account-secondary"}
                            />
                          }
                          label={config.secondaryAccountName}
                        />
                      )}
                    </RadioGroup>
                    {jobOperation.accounts.length === 0 && (
                      <FormHelperText error>Please select at least 1 account.</FormHelperText>
                    )}
                  </InputGroup>
                  <InputGroup label={`${config.primaryAccountName} Regions`}>
                    <RadioGroup value={jobOperation.operation} row={true}>
                      {config.regions.primary_regions.map((region) => (
                        <FormControlLabel
                          key={region}
                          value={region}
                          control={
                            <Checkbox
                              onChange={handlePrimaryRegionsChange}
                              checked={jobOperation.primary_regions.includes(region)}
                            />
                          }
                          label={region}
                        />
                      ))}
                    </RadioGroup>
                  </InputGroup>
                  {!config.singleTenantMode && (
                    <InputGroup label={`${config.secondaryAccountName} Regions`}>
                      <RadioGroup row={true}>
                        {config.regions.primary_regions.map((region) => (
                          <FormControlLabel
                            key={region}
                            value={region}
                            control={
                              <Checkbox
                                onChange={handleSecondaryRegionsChange}
                                checked={jobOperation.secondary_regions.includes(region)}
                              />
                            }
                            label={region}
                          />
                        ))}
                      </RadioGroup>
                    </InputGroup>
                  )}
                </>
              )}
              <InputGroup label="Profiles">
                <FormGroup>
                  {availableProfiles.map((profile) => (
                    <FormControlLabel
                      key={profile.profile}
                      sx={{ wordBreak: "break-all" }}
                      control={
                        <Checkbox
                          onChange={(e) => toggleProfile(profile, e.target.checked)}
                          checked={jobOperation.profiles.includes(profile)}
                          data-testid={`profile-${profile.profile}`}
                        />
                      }
                      label={truncateLabel(profile.profile)}
                    />
                  ))}
                </FormGroup>
                {jobOperation.profiles.length === 0 && (
                  <FormHelperText error>Please select at least 1 profile.</FormHelperText>
                )}
              </InputGroup>
              <InputGroup label="Options">
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={jobOperation.overwrite}
                        onChange={handleOverwriteExistingTranscodeChange}
                      />
                    }
                    label={"Overwrite Existing"}
                  />
                </FormGroup>
              </InputGroup>
            </CardContent>
          </Card>
        )}
        <Button
          variant="contained"
          onClick={onSearchClick}
          disabled={!jobOperation.validForBackfill()}
          data-testid={"search-button"}
        >
          Search
        </Button>
      </Grid>
    </Grid>
  );
};

export default CreateBackfillForm;
